import React, { createRef, useCallback, useEffect, useState } from 'react';
import { IconButton, Icons, LinkButton, Logo, Popover, Tooltip } from '@cambridgeassessment/cambridge-ui';
import { BusinessCentres, Role } from '@cambridgeassessment/lib-ee-commons';
import CheckIcon from '@material-ui/icons/Check';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { isEqual } from 'lodash';
import {
  LabelContainer,
  LogoContainer,
  PopoverContainer,
  RefContainer,
  SingleRoleMargin,
  StyledBUContainer,
  StyledCheckIcon,
  StyledContainer,
  StyledHeader,
  StyledListItem,
  StyledUserName,
  UserContainer,
  UserInfoContainer,
  useStyles,
} from './NavBar.styles';
import { RootState } from '../../store';
import { useUserName, useUserRoles } from '../../hooks';
import { setBusinessUnit, setUserSelectedRole } from '../../store/userData/actions';
import { updateItemInStorage } from '../../util/sessionStorage';
import { resetGlobalState } from '../../store/actions';
import { brandedFC } from '../../branding/brandedFC';
import { LogoProps } from '@cambridgeassessment/cambridge-ui/build/components/General/Logo/Logo';

const NavBar: React.FC = () => {
  const classes = useStyles();
  const popoverRef = createRef<HTMLDivElement>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { location } = history;
  const navigateHome = (): void => history.push('/');
  const isComponentReviewPage = location.pathname.includes('review-configure-component');
  const { selectedRole, businessUnit, availableBusinessUnits } = useSelector((state: RootState) => state.userData);
  const selectedRoleDescription = selectedRole && selectedRole.description;

  const [showUserRolePopover, setShowUserRolePopover] = useState(false);
  const [showBUPopover, setShowBUPopover] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const userName = useUserName();
  const userRoles = useUserRoles();
  const hasSingleRole: boolean = userRoles[0] && userRoles.length < 2;
  const hasMoreRoles: boolean = userRoles.length > 1;

  useEffect(() => {
    if (!selectedRole) {
      const roleName = sessionStorage.getItem('roleName');
      const roleFromSession = userRoles.find((role) => role.name === roleName) || userRoles[0];
      dispatch(setUserSelectedRole(roleFromSession));
    }
  }, [selectedRole, userRoles, dispatch]);

  const onLogoutClick = (): void => {
    updateItemInStorage('businessUnit');
    updateItemInStorage(`oidc.user:${window.env.SAP_CDC_DOMAIN}:${window.env.SAP_CDC_CLIENT_ID}`, null);
    window.location.href = `${window.env.SAP_CDC_LOGOUT}?redirect_uri=${window.location.origin}`;
  };

  const togglePopover = (): void => {
    setAnchorEl(popoverRef.current);
    setShowUserRolePopover(!showUserRolePopover);
    setShowBUPopover(false);
  };

  const onClosePopover = useCallback((): void => {
    setAnchorEl(null);
    setShowBUPopover(false);
    setShowUserRolePopover(false);
  }, []);

  const chooseUserRole = (role: Role): void => {
    updateItemInStorage('roleName', role.name);
    dispatch(setUserSelectedRole(role));
    setShowUserRolePopover(!showUserRolePopover);
    navigateHome();
  };

  const openBUList = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    setAnchorEl(e.currentTarget);
    setShowBUPopover(true);
    e.stopPropagation();
  }, []);

  const checkSelectedRole = (role: Role): boolean =>
    selectedRole ? selectedRole.name === role.name : userRoles[0].name === role.name;

  const renderLabel = (): React.ReactElement => (
    <LabelContainer data-qa-id='user-role'>{selectedRoleDescription || userRoles[0].description}</LabelContainer>
  );

  const renderBULabel = (): React.ReactElement => (
    <LabelContainer data-qa-id='user-business-unit'>{businessUnit && businessUnit.name}</LabelContainer>
  );

  const onBUSelect = (item: BusinessCentres): void => {
    if (isEqual(item, businessUnit)) {
      onClosePopover();
      return;
    }
    dispatch(setBusinessUnit(item));
    dispatch(resetGlobalState());
    onClosePopover();

    if (location.pathname !== '/') navigateHome();
  };

  const renderBUs = (): JSX.Element[] | JSX.Element => {
    return availableBusinessUnits.map((item, index) => {
      const selectedItem = item.id === businessUnit?.id;
      return (
        <StyledListItem
          data-qa-id={`${selectedItem ? 'selected' : 'select'}-option-${item.fullName}`}
          checked={selectedItem}
          key={index}
          onClick={(): void => onBUSelect(item)}
        >
          {item.fullName}
          {selectedItem && <StyledCheckIcon data-qa-id='selected-business-unit' className={classes.checkedOption} />}
        </StyledListItem>
      );
    });
  };

  const renderBUSelector = (): React.ReactElement => {
    return (
      <>
        {availableBusinessUnits.length === 1 ? (
          <SingleRoleMargin>{renderBULabel()}</SingleRoleMargin>
        ) : (
          availableBusinessUnits.length > 1 &&
          (isComponentReviewPage ? (
            <Tooltip
              arrow={true}
              dataQaId='confirm-component-config-BU'
              title='Confirm component configurations or leave the page'
              placement='top-start'
              enterDelay={500}
            >
              <span>
                <LinkButton
                  disabled={isComponentReviewPage}
                  onClick={openBUList}
                  dataQaId='select-business-unit'
                  arrowType={showBUPopover ? 'up' : 'down'}
                >
                  {renderBULabel()}
                </LinkButton>
              </span>
            </Tooltip>
          ) : (
            <LinkButton onClick={openBUList} dataQaId='select-business-unit' arrowType={showBUPopover ? 'up' : 'down'}>
              {renderBULabel()}
            </LinkButton>
          ))
        )}
        <Popover
          anchorEl={anchorEl}
          isOpen={showBUPopover}
          dataQaId='business-unit-select'
          onPopoverClose={onClosePopover}
          anchorOrigin={{
            vertical: 47,
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <StyledBUContainer>{renderBUs()}</StyledBUContainer>
        </Popover>
      </>
    );
  };

  const BrandedLogo = brandedFC<LogoProps>((brand, props) => <Logo {...props} type={brand.logo} />);

  return (
    <>
      <StyledHeader dataQaId='header'>
        <StyledContainer>
          <LogoContainer onClick={navigateHome} data-qa-id='header-logo'>
            <BrandedLogo width='160' height='36' />
          </LogoContainer>
          {businessUnit && renderBUSelector()}
        </StyledContainer>
        <UserContainer>
          <UserInfoContainer>
            <StyledUserName data-qa-id={'user-name'}>{userName}</StyledUserName>
            {hasSingleRole ? (
              <SingleRoleMargin>{renderLabel()}</SingleRoleMargin>
            ) : (
              hasMoreRoles &&
              (isComponentReviewPage ? (
                <Tooltip
                  arrow={true}
                  dataQaId='confirm-component-config-role'
                  title='Confirm component configurations or leave the page to change the role'
                  placement='top-start'
                  enterDelay={500}
                >
                  <span>
                    <LinkButton onClick={togglePopover} disabled dataQaId='select-user-role' arrowType={'down'}>
                      {renderLabel()}
                    </LinkButton>
                  </span>
                </Tooltip>
              ) : (
                <LinkButton
                  onClick={togglePopover}
                  dataQaId='select-user-role'
                  arrowType={showUserRolePopover ? 'up' : 'down'}
                >
                  {renderLabel()}
                </LinkButton>
              ))
            )}
            <Popover dataQaId='popover-user-role' isOpen={showUserRolePopover} anchorEl={anchorEl}>
              <ClickAwayListener onClickAway={togglePopover}>
                <PopoverContainer>
                  {userRoles.map((role, key) => {
                    const checked = checkSelectedRole(role);
                    return (
                      <StyledListItem
                        data-qa-id={`dropdown-role-${role.description}`}
                        checked={checked}
                        key={key}
                        onClick={() => chooseUserRole(role)}
                      >
                        <>
                          {role.description}
                          {checked && (
                            <CheckIcon data-qa-id={`selected-role-icon-${checked}`} className={classes.checkedOption} />
                          )}
                        </>
                      </StyledListItem>
                    );
                  })}
                </PopoverContainer>
              </ClickAwayListener>
            </Popover>
          </UserInfoContainer>
          <IconButton color='primary' onClick={onLogoutClick} qaId='button-logout'>
            <Icons.Logout width={22} height={20} />
          </IconButton>
        </UserContainer>
      </StyledHeader>
      <RefContainer data-qa-id='ref-container' ref={popoverRef} />
    </>
  );
};

export default NavBar;
